import React, { useState, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FeedbackToolWrapper } from '../Shared/FeedbackTool';
import ImprovedSecondarySideBar from '../ImprovedSecondarySideBar';
import MenuButton from '../MenuButton';
import { isTabletView } from '../ReactiveRender';
import SuccessCriteriaCard from './SuccessCriteriaCard';
import SuccessCriteriaDialog from '../Shared/Dialogs/successCriteria';
import {
  PopUpCard,
  PopUpCardImg,
  PopUpCardText,
} from '../Shared/FeedbackTool/style';
import {
  PopupBackground,
  PopupContainer,
  PopupTitleContainer,
  PopupTitle,
  PopupTitleImg,
  PopupDialogContentBox,
  PopupDialogContentBoxLeft,
  PopupDialogContentBoxRight,
  CardContainer,
  Card,
  CardImgCont,
  CardImg,
  CardTitle,
  ImportFileLabel,
  ImportFile,
  CardImgDoc,
  BankCommentTitle,
  PreviewContainer,
  PreviewIconCont,
  PrevieImg,
  Previewpara,
  CreateButton,
  ButtonText,
  Commentsuggestion,
} from '../Shared/Dialogs/popupStyle';
import {
  importJsonFile,
  exportJsonFile,
} from '../../components2/markingCriteria';
import { isNullOrEmpty } from '../../utils/arrays';
import {
  createSuccessCriteria,
  deleteSuccessCriteria,
  getSuccessCriteria,
  updateSuccessCriteriaPreferences,
} from '../../service';
import { buildClonePayload } from '../../utils/subjects';
import { useApiSubjects } from '../../hooks/useSubjects';
import {
  useSuccessCriteria,
  useSuccessCriteriaWithPreferences,
} from '../state/hooks';
import Loader from '../Loader';
import PlusIcon from '../../static/img/Plus-blue.svg';
import DownloadIcon from '../../static/img/Download.svg';
import closecircle from '../../static/img/closecircle.svg';
import Doc from '../../static/img/doc.svg';
import PreviewIcon from '../../static/img/preview.svg';
import UploadIcon from '../../static/icons/share-download-icon.svg';

function SuccessCriteria() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [selectedSuccessCriteria, setSelectedSuccessCriteria] = useState(
    new Set()
  );
  const [showHidden, setShowHidden] = useState(false);
  const [isShowSystemSuccessCriteriaOnly, setIsShowSystemSuccessCriteriaOnly] =
    useState(false);
  const [importedSuccessCriteria, setImportedSuccessCriteria] = useState({});
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState();
  const [successCriteriaPreviewDialog, setSuccessCriteriaPreviewDialog] =
    useState(false);
  const [previewSuccessCriteria, setPreviewSuccessCriteria] = useState(null);
  const selectedRef = useRef(null);
  const tabletView = isTabletView();

  const {
    data: successCriteriaData,
    isLoadingdata: isLoadingSuccessCriteria,
    setData: setSuccessCriteria,
    resetData: resetSuccessCriteria,
  } = useSuccessCriteria();

  const {
    data: successCriteriaWithPreferences,
    isLoadingdata: isLoadingSuccessCriteriaWithPreferences,
    setData: setSuccessCriteriaWithPreferences,
    resetData: resetSuccessCriteriaWithPreferences,
  } = useSuccessCriteriaWithPreferences();
  const { apiSubjects } = useApiSubjects();

  const hasNoData = !successCriteriaData && !successCriteriaWithPreferences;
  const originalSuccessCriteria = Array.isArray(successCriteriaData)
    ? successCriteriaData
    : [];
  const successCriteriaPreferencesArray = Array.isArray(
    successCriteriaWithPreferences
  )
    ? successCriteriaWithPreferences
    : [];
  const systemSuccessCriteria = originalSuccessCriteria.filter(
    (criteria) => criteria.isSystem
  );

  const hasActiveFilters = useMemo(() => {
    return isShowSystemSuccessCriteriaOnly || showHidden;
  }, [isShowSystemSuccessCriteriaOnly, showHidden]);

  const isDragDisabled =
    hasActiveFilters || isLoadingSuccessCriteriaWithPreferences;

  const handleSelectSuccessCriteria = (successCriteriaId) => {
    const newSelected = new Set(selectedSuccessCriteria);
    if (newSelected.has(successCriteriaId)) {
      newSelected.delete(successCriteriaId);
    } else {
      newSelected.add(successCriteriaId);
    }
    setSelectedSuccessCriteria(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSuccessCriteria.size === (filteredItems?.length || 0)) {
      setSelectedSuccessCriteria(new Set());
    } else {
      const allIds = filteredItems?.map((sc) => sc.id) || [];
      setSelectedSuccessCriteria(new Set(allIds));
    }
  };

  const handleSuccessCriteriaImport = async (event) => {
    try {
      const importedJsonFile = await importJsonFile(event, ['sc']);
      setImportedSuccessCriteria(importedJsonFile);
      setShowImportDialog(true);
    } catch (err) {
      console.error('Error importing success criteria:', err);
    }
  };

  const createNewSuccessCriteria = (
    successCriteria = undefined,
    onClose = null
  ) => {
    const newCriteria = {
      title: 'Untitled Success Criterias',
      criterias: [
        {
          objectives: [
            {
              description: 'First objective',
            },
          ],
          selectedStatus: null,
        },
      ],
    };

    createSuccessCriteria(successCriteria || newCriteria)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'successCriteria'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'],
        });
        resetSuccessCriteria();
        resetSuccessCriteriaWithPreferences();

        if (onClose) {
          onClose();
        }

        const selfLink = response?._links?.self?.href || response?.id;
        if (selfLink) {
          const newSuccessCriteriaId =
            typeof selfLink === 'string' && selfLink.includes('/')
              ? selfLink.split('/').pop()
              : selfLink;
          history.push(`/tools/successcriterias/${newSuccessCriteriaId}`);
        }
      })
      .catch((error) => {
        console.error('Error creating new success criteria:', error);
      });
  };

  const createSystemSuccessCriteria = (successCriteria, onClose = null) => {
    if (!successCriteria) {
      return;
    }

    const { criterias } = successCriteria;
    const newObject = buildClonePayload(successCriteria, apiSubjects, {
      criterias,
    });

    createSuccessCriteria(newObject)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'successCriteria'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'],
        });

        resetSuccessCriteria();
        resetSuccessCriteriaWithPreferences();

        if (onClose) {
          onClose();
        }

        const selfLink = response?._links?.self?.href || response?.id;
        if (selfLink) {
          const newSuccessCriteriaId =
            typeof selfLink === 'string' && selfLink.includes('/')
              ? selfLink.split('/').pop()
              : selfLink;
          history.push(`/tools/successcriterias/${newSuccessCriteriaId}`);
        }
      })
      .catch((error) => {
        console.error('Error creating new success criteria:', error);
      });
  };

  const cloneSuccessCriteria = async (successCriteria) => {
    const cleanedCriterias =
      successCriteria.criterias?.map((criteria) => ({
        objectives:
          criteria.objectives?.map((obj) => ({
            description: obj.description,
          })) || [],
        selectedStatus: null,
      })) || [];
    const source = successCriteria.id
      ? await getSuccessCriteria(successCriteria.id)
          .then((data) =>
            Array.isArray(data)
              ? data.find((i) => i.id === successCriteria.id) || data[0]
              : data
          )
          .catch(() => successCriteria)
      : successCriteria;
    const newObject = buildClonePayload(source, apiSubjects, {
      criterias: cleanedCriterias,
    });
    console.log('[clone success criteria] source (for metadata):', source);
    console.log('[clone success criteria] API payload:', newObject);

    createSuccessCriteria(newObject)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'successCriteria'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'],
        });
        resetSuccessCriteria();
        resetSuccessCriteriaWithPreferences();
      })
      .catch((error) => {
        console.error('Error cloning success criteria:', error);
      });
  };

  const downloadSuccessCriteria = (successCriteria) => {
    const extractedSuccessCriteria = {
      title: successCriteria.title,
      criterias: successCriteria.criterias,
    };
    exportJsonFile(
      extractedSuccessCriteria,
      extractedSuccessCriteria.title,
      'sc'
    );
  };

  const handleDownload = (item) => {
    if (item) {
      const extractedSuccessCriteria = {
        title: item.title,
        criterias: item.criterias,
      };
      exportJsonFile(
        extractedSuccessCriteria,
        extractedSuccessCriteria.title,
        'sc'
      );
    }
  };

  const handleClone = async (successCriteria) => {
    if (successCriteria) {
      const cleanedCriterias =
        successCriteria.criterias?.map((criteria) => ({
          objectives:
            criteria.objectives?.map((obj) => ({
              description: obj.description,
            })) || [],
          selectedStatus: null,
        })) || [];
      const source = successCriteria.id
        ? await getSuccessCriteria(successCriteria.id)
            .then((data) =>
              Array.isArray(data)
                ? data.find((i) => i.id === successCriteria.id) || data[0]
                : data
            )
            .catch(() => successCriteria)
        : successCriteria;
      const newObject = buildClonePayload(source, apiSubjects, {
        criterias: cleanedCriterias,
      });
      console.log('[clone success criteria] source (for metadata):', source);
      console.log('[clone success criteria] API payload:', newObject);

      createSuccessCriteria(newObject)
        .then(async (res) => {
          const selfLink = res?._links?.self?.href || res?.id;
          let newSuccessCriteriaId = null;
          if (selfLink) {
            if (typeof selfLink === 'string' && selfLink.includes('/')) {
              const urlParts = selfLink.split('/');
              newSuccessCriteriaId = urlParts[urlParts.length - 1];
            } else {
              newSuccessCriteriaId = selfLink;
            }
          }
          if (newSuccessCriteriaId) {
            const originalSortOrder = successCriteria.sortOrder ?? 999;
            const newSortOrder = originalSortOrder + 1;
            try {
              await updateSuccessCriteriaPreferences('REORDER', {
                toolIds: [newSuccessCriteriaId],
                newSortOrder,
              });
            } catch (error) {
              console.error(
                'Error setting sort order for duplicated success criteria:',
                error
              );
            }
          }
          queryClient.invalidateQueries({
            queryKey: ['feedbackTool', 'items', 'successCriteria'],
          });
          queryClient.invalidateQueries({
            queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'],
          });
          resetSuccessCriteria();
          resetSuccessCriteriaWithPreferences();
        })
        .catch((error) => {
          console.error('Cloning error:', error);
        });
    }
  };

  const handlePreview = (successCriteria) => {
    if (successCriteria && !isNullOrEmpty(successCriteria?.criterias)) {
      setSuccessCriteriaPreviewDialog(true);
      setPreviewSuccessCriteria(successCriteria);
    }
  };

  const handleOpenClick = (successCriteria) => {
    if (successCriteria && !successCriteria.isSystem) {
      history.push(`/tools/successcriterias/${successCriteria.id}`);
    }
  };

  const deleteSuccessCriteriaHandler = (successCriteriaId) => {
    deleteSuccessCriteria(successCriteriaId)
      .then(() => {
        resetSuccessCriteria();
        resetSuccessCriteriaWithPreferences();
      })
      .catch((error) => {
        console.error('Error deleting success criteria:', error);
      });
  };

  const renderCreateOptions = () => (
    <>
      <PopUpCard onClick={() => history.push('/tools/successcriterias/new')}>
        <PopUpCardImg src={PlusIcon} />
        <PopUpCardText>Create Your Own</PopUpCardText>
      </PopUpCard>
      <PopUpCard onClick={() => setShowImportDialog(true)}>
        <PopUpCardImg src={DownloadIcon} />
        <PopUpCardText>Import</PopUpCardText>
      </PopUpCard>
      <PopUpCard
        onClick={() =>
          createSystemSuccessCriteria({
            title: 'System Success Criterias',
            criterias: [],
            isSystem: true,
          })
        }
      >
        <PopUpCardImg src={PlusIcon} />
        <PopUpCardText>Use a template</PopUpCardText>
      </PopUpCard>
    </>
  );

  const renderImportDialog = () => {
    if (showImportDialog) {
      return (
        <SuccessCriteriaDialog
          successCriteria={importedSuccessCriteria}
          setSuccessCriteriaPreviewDialog={setShowImportDialog}
          showActionButton={true}
          onActionButtonClick={() => {
            setShowImportDialog(false);
            createNewSuccessCriteria(importedSuccessCriteria);
          }}
        />
      );
    }
    return null;
  };

  const renderCreatePopup = ({ onClose }) => {
    const selectCriteriaFun = (e, criteria) => {
      e.preventDefault();
      setSelectedCriteria(criteria);
      setTimeout(() => {
        if (selectedRef.current) {
          selectedRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 0);
    };

    return (
      <PopupBackground>
        <PopupContainer>
          <PopupTitleContainer>
            <PopupTitle>New Success Criteria</PopupTitle>
            <PopupTitleImg onClick={onClose} src={closecircle} />
          </PopupTitleContainer>
          <PopupDialogContentBox>
            <PopupDialogContentBoxLeft>
              <CardContainer>
                <Card
                  onClick={() => history.push('/tools/successcriterias/new')}
                >
                  <CardImgCont>
                    <CardImg src={PlusIcon} />
                  </CardImgCont>
                  <CardTitle>Create Your Own</CardTitle>
                </Card>
                <ImportFileLabel>
                  <CardImgCont>
                    <CardImg src={UploadIcon} />
                  </CardImgCont>
                  <CardTitle>Import</CardTitle>
                  <ImportFile
                    type="file"
                    accept=".sc"
                    onChange={handleSuccessCriteriaImport}
                  />
                </ImportFileLabel>
              </CardContainer>
              {systemSuccessCriteria && systemSuccessCriteria.length > 0 && (
                <BankCommentTitle style={{ borderTop: '1px solid #C9C6CC80' }}>
                  Use a template
                </BankCommentTitle>
              )}

              {systemSuccessCriteria?.map((criteria) => {
                return (
                  <Card
                    key={criteria.id}
                    onClick={(e) => selectCriteriaFun(e, criteria)}
                    ref={
                      criteria.id === selectedCriteria?.id ? selectedRef : null
                    }
                    style={{
                      backgroundColor:
                        criteria.id === selectedCriteria?.id
                          ? ' #F1E6FC'
                          : '#ffffff',
                    }}
                  >
                    <CardImgDoc src={Doc} />
                    <CardTitle>{criteria.title}</CardTitle>
                  </Card>
                );
              })}
            </PopupDialogContentBoxLeft>
            <PopupDialogContentBoxRight>
              <PreviewContainer>
                <PreviewIconCont>
                  <PrevieImg src={PreviewIcon} />
                  <Previewpara>Preview</Previewpara>
                </PreviewIconCont>
                {systemSuccessCriteria &&
                  systemSuccessCriteria.length > 0 &&
                  selectedCriteria && (
                    <CreateButton
                      onClick={() => {
                        createSystemSuccessCriteria(selectedCriteria, onClose);
                      }}
                    >
                      <ButtonText>Add selected template</ButtonText>
                    </CreateButton>
                  )}
              </PreviewContainer>

              {selectedCriteria?.criterias?.map((criteria, idx) => {
                return (
                  <div key={idx}>
                    {criteria?.objectives?.map((objective) => (
                      <Commentsuggestion key={objective.description}>
                        {objective.description}
                      </Commentsuggestion>
                    ))}
                  </div>
                );
              })}
            </PopupDialogContentBoxRight>
          </PopupDialogContentBox>
        </PopupContainer>
      </PopupBackground>
    );
  };

  const renderItem = (successCriteria) => (
    <SuccessCriteriaCard
      successCriteria={successCriteria}
      isSelected={selectedSuccessCriteria.has(successCriteria.id)}
      onSelect={() => handleSelectSuccessCriteria(successCriteria.id)}
      showCheckbox={true}
      showActions={true}
      isSystem={successCriteria.isSystem}
      cloneSuccessCriteria={cloneSuccessCriteria}
      handleDownload={handleDownload}
      handleClone={handleClone}
      handlePreview={handlePreview}
      handleOpenClick={handleOpenClick}
    />
  );

  const mergedSuccessCriteria = originalSuccessCriteria.map((criteria) => {
    const preference = successCriteriaPreferencesArray.find(
      (pref) => pref.id === criteria.id
    );
    const merged = preference
      ? {
          ...criteria,
          isVisible: preference.isVisible,
          visible:
            preference.visible !== false && preference.isVisible !== false,
          sortOrder: preference.sortOrder,
        }
      : criteria;
    return merged;
  });

  const filteredItems = mergedSuccessCriteria.filter((successCriteria) => {
    if (isShowSystemSuccessCriteriaOnly) {
      if (successCriteria.isSystem) {
        return false;
      }
    }

    if (showHidden) {
      return !successCriteria.visible;
    } else {
      return successCriteria.visible;
    }
  });

  const resetSuccessCriteriaData = () => {
    resetSuccessCriteria();
    resetSuccessCriteriaWithPreferences();
  };

  // Removed full-page loader to prevent the flash that the user saw on
  // /tools/rubrics — see AccountSettingRoot for the full rationale. Same
  // pattern: Loader is position:fixed and covered the page chrome while
  // data loaded, so the user perceived an unwanted second full-page load.
  // The page chrome handles empty state safely.
  // const showLoader = hasNoData && (isLoadingSuccessCriteria || isLoadingSuccessCriteriaWithPreferences);
  // if (showLoader) return <Loader />;

  return (
    <>
      <FeedbackToolWrapper
        enableReactQuery={true}
        context="successCriteria"
        selectedItems={selectedSuccessCriteria}
        setSelectedCriteria={setSelectedSuccessCriteria}
        onImport={handleSuccessCriteriaImport}
        onSelectAll={handleSelectAll}
        renderItem={renderItem}
        renderCreateOptions={renderCreateOptions}
        renderImportDialog={renderImportDialog}
        isDragDisabled={isDragDisabled}
        hasActiveFilters={hasActiveFilters}
        tabletView={tabletView}
        MenuButton={MenuButton}
        SecondarySidebar={ImprovedSecondarySideBar}
        showHidden={showHidden}
        setShowHidden={setShowHidden}
        showOnlyMineFilter={isShowSystemSuccessCriteriaOnly}
        setShowOnlyMineFilter={setIsShowSystemSuccessCriteriaOnly}
        allMarkingCriteria={mergedSuccessCriteria}
        resetMarkingCriterias={resetSuccessCriteriaData}
        usePopupForCreate={true}
        renderCreatePopup={renderCreatePopup}
      />
      {successCriteriaPreviewDialog && previewSuccessCriteria && (
        <SuccessCriteriaDialog
          setSuccessCriteriaPreviewDialog={setSuccessCriteriaPreviewDialog}
          successCriteria={previewSuccessCriteria}
          isSystem={previewSuccessCriteria?.isSystem}
        />
      )}
    </>
  );
}

export default SuccessCriteria;
